/**
 * ComparisonService — Transforms raw JSON blobs from MenuChangeRequest
 * into structured "Before vs After" UI models for the approval inbox.
 *
 * Zero JSON in the UI. Every field is labeled, typed, and classified.
 */

import type { MenuChangeRequest } from '../types';
import { getMediaUrl } from '../../../core/fileUpload';

// ── Public Types ─────────────────────────────────────────────────────────── //

export type ChangeCategory = 'PRICE_CHANGE' | 'TEXT_UPDATE' | 'IMAGE_UPDATE' | 'VISIBILITY' | 'DELETION' | 'NEW_ITEM' | 'OPTION_GROUP' | 'MIXED';

export interface ComparisonField {
  key: string;
  label: string;
  /** Rendered value (human-readable, never raw JSON) */
  currentValue: string;
  proposedValue: string;
  changed: boolean;
  /** Highlight style: 'modified' | 'added' | 'removed' | 'none' */
  highlight: 'modified' | 'added' | 'removed' | 'none';
}

export interface ImageComparison {
  currentUrl: string | undefined;
  proposedUrl: string | undefined;
  changed: boolean;
}

export interface OptionGroupComparison {
  id: string;
  name: string;
  status: 'added' | 'removed' | 'modified' | 'unchanged';
  fields: ComparisonField[];
  options: { name: string; price: string; status: 'added' | 'removed' | 'unchanged' }[];
}

export interface ComparisonModel {
  /** Display name of the entity being changed */
  entityName: string;
  /** Entity type label */
  entityTypeLabel: string;
  /** Overall change category for badge */
  changeCategory: ChangeCategory;
  /** Human-readable change type */
  changeTypeLabel: string;
  /** Is this a deletion request? */
  isDeletion: boolean;
  /** Is this a new addition? */
  isAddition: boolean;
  /** Structured field comparisons */
  fields: ComparisonField[];
  /** Image comparison if applicable */
  image: ImageComparison | null;
  /** Option group comparisons for items */
  optionGroups: OptionGroupComparison[];
  /** Raw parsed current (live) entity */
  currentEntity: Record<string, unknown>;
  /** Raw parsed proposed entity */
  proposedEntity: Record<string, unknown>;
}

// ── Field Label Map ──────────────────────────────────────────────────────── //

const FIELD_LABELS: Record<string, string> = {
  nameEN: 'Name (English)',
  nameAR: 'Name (Arabic)',
  name: 'Name',
  brandName: 'Brand',
  createdByName: 'Created By',
  createdAt: 'Created On',
  updatedAt: 'Updated On',
  categoryCount: 'Categories',
  itemCount: 'Items',
  branchCount: 'Assigned Branches',
  basePrice: 'Price',
  cost: 'Cost',
  descriptionEN: 'Description (English)',
  descriptionAR: 'Description (Arabic)',
  hidden: 'Visibility',
  calories: 'Calories',
  sku: 'SKU',
  barcode: 'Barcode',
  tag: 'Tag',
  sortOrder: 'Sort Order',
  minSelect: 'Min Selection',
  maxSelect: 'Max Selection',
  required: 'Required',
  allowMultiple: 'Allow Multiple',
};

/** Keys to skip entirely (internal IDs, not user-facing) */
const SKIP_KEYS = new Set([
  'id', 'categoryIds', 'sortOrder', 'optionGroups', 'image',
  'libraryOptionId', 'globalHiddenOptionIds', 'connectedModules',
]);

// ── Core Builder ─────────────────────────────────────────────────────────── //

/**
 * Build a ComparisonModel from a MenuChangeRequest.
 * Works for entity-level (item/category/optionGroup) requests.
 */
export function buildComparison(req: MenuChangeRequest): ComparisonModel {
  const changeType = req.changeType || 'MODIFY';
  const entityType = req.entityType || 'item';
  const isDeletion = changeType === 'DELETE';
  const isAddition = changeType === 'ADD' || changeType === 'CREATE_PROFILE';

  // Parse JSON blobs safely
  const current = safeParse(req.previousData);
  const proposed = safeParse(req.entityData || req.changes);

  const entityName = getEntityName(proposed, current);
  const entityTypeLabel = entityType === 'item' ? 'Product'
    : entityType === 'category' ? 'Category'
    : entityType === 'optionGroup' ? 'Option Group'
    : entityType === 'profile' ? 'Menu Profile'
    : 'Entity';

  // Build field comparisons
  const fields = buildFieldComparisons(current, proposed, isDeletion, isAddition);

  // Detect image changes
  const image = buildImageComparison(current, proposed);

  // Build option group comparisons (for items only)
  const optionGroups = entityType === 'item'
    ? buildOptionGroupComparisons(current, proposed)
    : [];

  // Classify the change
  const changeCategory = classifyChange(fields, image, isDeletion, isAddition, optionGroups);

  const changeCategoryLabels: Record<ChangeCategory, string> = {
    PRICE_CHANGE: 'Price Change',
    TEXT_UPDATE: 'Text Update',
    IMAGE_UPDATE: 'Image Update',
    VISIBILITY: 'Visibility Toggle',
    DELETION: 'Deletion',
    NEW_ITEM: 'New Addition',
    OPTION_GROUP: 'Modifier Update',
    MIXED: 'Multiple Changes',
  };

  const changeTypeLabel = changeType === 'CREATE_PROFILE'
    ? 'New Profile'
    : changeCategoryLabels[changeCategory];

  return {
    entityName,
    entityTypeLabel,
    changeCategory,
    changeTypeLabel,
    isDeletion,
    isAddition,
    fields,
    image,
    optionGroups,
    currentEntity: current,
    proposedEntity: proposed,
  };
}

// ── Field Comparison Builder ─────────────────────────────────────────────── //

function buildFieldComparisons(
  current: Record<string, unknown>,
  proposed: Record<string, unknown>,
  isDeletion: boolean,
  isAddition: boolean,
): ComparisonField[] {
  const allKeys = new Set([...Object.keys(current), ...Object.keys(proposed)]);
  const fields: ComparisonField[] = [];

  for (const key of allKeys) {
    if (SKIP_KEYS.has(key)) continue;

    const label = FIELD_LABELS[key] || prettifyKey(key);
    const rawOld = current[key];
    const rawNew = proposed[key];

    // Skip complex nested objects (handled separately)
    if (isComplexValue(rawOld) || isComplexValue(rawNew)) continue;

    const currentValue = formatValue(key, rawOld);
    const proposedValue = formatValue(key, rawNew);
    const changed = currentValue !== proposedValue;

    let highlight: ComparisonField['highlight'] = 'none';
    if (isDeletion) highlight = 'removed';
    else if (isAddition && proposedValue) highlight = 'added';
    else if (changed) highlight = 'modified';

    // Only include if there's content or a change
    if (changed || currentValue || proposedValue) {
      fields.push({ key, label, currentValue, proposedValue, changed, highlight });
    }
  }

  return fields;
}

// ── Image Comparison ─────────────────────────────────────────────────────── //

function buildImageComparison(
  current: Record<string, unknown>,
  proposed: Record<string, unknown>,
): ImageComparison | null {
  const oldImg = current.image as string | undefined;
  const newImg = proposed.image as string | undefined;

  if (!oldImg && !newImg) return null;

  return {
    currentUrl: oldImg ? getMediaUrl(oldImg) : undefined,
    proposedUrl: newImg ? getMediaUrl(newImg) : undefined,
    changed: (oldImg || '') !== (newImg || ''),
  };
}

// ── Option Group Comparisons ─────────────────────────────────────────────── //

function buildOptionGroupComparisons(
  current: Record<string, unknown>,
  proposed: Record<string, unknown>,
): OptionGroupComparison[] {
  const oldGroups = (current.optionGroups || []) as any[];
  const newGroups = (proposed.optionGroups || []) as any[];

  if (oldGroups.length === 0 && newGroups.length === 0) return [];

  const oldMap = new Map(oldGroups.map((g: any) => [g.id, g]));
  const newMap = new Map(newGroups.map((g: any) => [g.id, g]));
  const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
  const result: OptionGroupComparison[] = [];

  for (const id of allIds) {
    const a = oldMap.get(id);
    const b = newMap.get(id);

    if (!a && b) {
      result.push({
        id, name: b.nameEN || b.name || 'Group',
        status: 'added', fields: [],
        options: (b.options || []).map((o: any) => ({
          name: o.nameEN || o.name || 'Option',
          price: o.price != null ? `${o.price}` : '',
          status: 'added' as const,
        })),
      });
    } else if (a && !b) {
      result.push({
        id, name: a.nameEN || a.name || 'Group',
        status: 'removed', fields: [],
        options: (a.options || []).map((o: any) => ({
          name: o.nameEN || o.name || 'Option',
          price: o.price != null ? `${o.price}` : '',
          status: 'removed' as const,
        })),
      });
    } else if (a && b) {
      const fields: ComparisonField[] = [];
      if (a.nameEN !== b.nameEN) fields.push({ key: 'nameEN', label: 'Name (EN)', currentValue: a.nameEN || '', proposedValue: b.nameEN || '', changed: true, highlight: 'modified' });
      if (a.nameAR !== b.nameAR) fields.push({ key: 'nameAR', label: 'Name (AR)', currentValue: a.nameAR || '', proposedValue: b.nameAR || '', changed: true, highlight: 'modified' });
      if (a.minSelect !== b.minSelect) fields.push({ key: 'minSelect', label: 'Min Select', currentValue: `${a.minSelect ?? 0}`, proposedValue: `${b.minSelect ?? 0}`, changed: true, highlight: 'modified' });
      if (a.maxSelect !== b.maxSelect) fields.push({ key: 'maxSelect', label: 'Max Select', currentValue: `${a.maxSelect ?? 0}`, proposedValue: `${b.maxSelect ?? 0}`, changed: true, highlight: 'modified' });

      // Compare options
      const oldOpts = new Map((a.options || []).map((o: any) => [o.id || o.nameEN, o]));
      const newOpts = new Map((b.options || []).map((o: any) => [o.id || o.nameEN, o]));
      const allOptIds = new Set([...oldOpts.keys(), ...newOpts.keys()]);
      const options: OptionGroupComparison['options'] = [];
      for (const oid of allOptIds) {
        const oa = oldOpts.get(oid) as any;
        const ob = newOpts.get(oid) as any;
        if (!oa && ob) options.push({ name: ob.nameEN || ob.name || 'Option', price: ob.price != null ? `${ob.price}` : '', status: 'added' });
        else if (oa && !ob) options.push({ name: oa.nameEN || oa.name || 'Option', price: oa.price != null ? `${oa.price}` : '', status: 'removed' });
        else options.push({ name: (ob || oa).nameEN || (ob || oa).name || 'Option', price: (ob || oa).price != null ? `${(ob || oa).price}` : '', status: 'unchanged' });
      }

      const hasChanges = fields.length > 0 || options.some(o => o.status !== 'unchanged');
      if (hasChanges) {
        result.push({ id, name: b.nameEN || b.name || 'Group', status: 'modified', fields, options });
      }
    }
  }

  return result;
}

// ── Classification ───────────────────────────────────────────────────────── //

function classifyChange(
  fields: ComparisonField[],
  image: ImageComparison | null,
  isDeletion: boolean,
  isAddition: boolean,
  optionGroups: OptionGroupComparison[],
): ChangeCategory {
  if (isDeletion) return 'DELETION';
  if (isAddition) return 'NEW_ITEM';

  const changedFields = fields.filter(f => f.changed);
  const hasPrice = changedFields.some(f => f.key === 'basePrice' || f.key === 'cost');
  const hasText = changedFields.some(f => ['nameEN', 'nameAR', 'descriptionEN', 'descriptionAR', 'name'].includes(f.key));
  const hasImage = image?.changed ?? false;
  const hasVisibility = changedFields.some(f => f.key === 'hidden');
  const hasGroupChanges = optionGroups.length > 0;

  const categories = [hasPrice, hasText, hasImage, hasVisibility, hasGroupChanges].filter(Boolean).length;
  if (categories > 1) return 'MIXED';
  if (hasPrice) return 'PRICE_CHANGE';
  if (hasText) return 'TEXT_UPDATE';
  if (hasImage) return 'IMAGE_UPDATE';
  if (hasVisibility) return 'VISIBILITY';
  if (hasGroupChanges) return 'OPTION_GROUP';
  return 'TEXT_UPDATE'; // fallback
}

// ── Helpers ──────────────────────────────────────────────────────────────── //

function safeParse(json?: string | null): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    // Guard against JSON.parse("null") → null or non-object results
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch { return {}; }
}

function getEntityName(proposed: Record<string, unknown>, current: Record<string, unknown>): string {
  return (proposed.nameEN || proposed.name || current.nameEN || current.name || 'Unnamed') as string;
}

function prettifyKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
}

function formatValue(key: string, value: unknown): string {
  if (value == null || value === '') return '';
  if (key === 'hidden') return value ? 'Hidden' : 'Visible';
  if ((key === 'basePrice' || key === 'cost') && typeof value === 'number') return value.toFixed(2);
  if ((key === 'createdAt' || key === 'updatedAt') && typeof value === 'number') return new Date(value).toLocaleString();
  if (key === 'required') return value ? 'Yes' : 'No';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return String(value);
}

function isComplexValue(value: unknown): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return true;
  if (typeof value === 'object') return true;
  return false;
}
